import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UtensilsCrossed, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanoAlimentar = () => {
  const [mealPlan, setMealPlan] = useState(null);
  const [checkedMeals, setCheckedMeals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMealPlan();
    loadCheckedMeals();
  }, []);

  const fetchMealPlan = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/meal-plans/current`);
      setMealPlan(response.data);
    } catch (error) {
      console.error('Erro ao carregar plano:', error);
      toast.error('Erro ao carregar plano alimentar');
    } finally {
      setLoading(false);
    }
  };

  // Função para obter data local (evita problema de timezone)
  const getLocalDateString = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const loadCheckedMeals = () => {
    const today = getLocalDateString();
    const saved = localStorage.getItem(`meals_${today}`);
    if (saved) {
      setCheckedMeals(JSON.parse(saved));
    }
  };

  const toggleMeal = (mealIndex) => {
    const today = getLocalDateString();
    const newChecked = {
      ...checkedMeals,
      [mealIndex]: !checkedMeals[mealIndex]
    };
    setCheckedMeals(newChecked);
    localStorage.setItem(`meals_${today}`, JSON.stringify(newChecked));
    
    if (!checkedMeals[mealIndex]) {
      toast.success('Refeição marcada como cumprida!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  if (!mealPlan) {
    return (
      <Layout>
        <div className="text-center py-12">
          <UtensilsCrossed className="mx-auto text-muted-foreground mb-4" size={64} />
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Nenhum Plano Alimentar
          </h2>
          <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Seu médico ainda não criou um plano alimentar para você
          </p>
        </div>
      </Layout>
    );
  }

  const totalMeals = mealPlan.refeicoes.length;
  const completedMeals = Object.values(checkedMeals).filter(Boolean).length;
  const progressPercentage = (completedMeals / totalMeals) * 100;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Plano Alimentar
          </h2>
          <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Siga seu plano personalizado
          </p>
        </div>

        {/* Progress Card */}
        <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Progresso do Dia
            </h3>
            <span className="text-primary font-bold text-lg">
              {completedMeals}/{totalMeals}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className="bg-primary rounded-full h-3 transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Orientações */}
        {mealPlan.orientacoes && (
          <div className="bg-secondary/50 rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Orientações do Nutricionista
            </h3>
            <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {mealPlan.orientacoes}
            </p>
          </div>
        )}

        {/* Meal List */}
        <div className="space-y-4">
          {mealPlan.refeicoes.map((refeicao, index) => (
            <div
              key={index}
              data-testid={`meal-card-${index}`}
              className="bg-card rounded-2xl shadow-sm border border-border p-6"
            >
              <div className="flex items-start gap-4">
                <button
                  data-testid={`meal-check-${index}`}
                  onClick={() => toggleMeal(index)}
                  className="flex-shrink-0 mt-1"
                >
                  {checkedMeals[index] ? (
                    <CheckCircle2 className="text-primary" size={28} />
                  ) : (
                    <Circle className="text-muted-foreground" size={28} />
                  )}
                </button>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {refeicao.horario}
                    </span>
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {refeicao.nome}
                    </h3>
                  </div>

                  <ul className="space-y-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {refeicao.alimentos.map((alimento, idx) => (
                      <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{alimento}</span>
                      </li>
                    ))}
                  </ul>

                  {refeicao.calorias && (
                    <div className="mt-3 text-sm text-muted-foreground">
                      Calorias aproximadas: {refeicao.calorias} kcal
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dicas */}
        <div className="bg-accent/10 rounded-2xl border border-accent/20 p-6">
          <h3 className="text-lg font-semibold mb-3 text-accent" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dicas Importantes
          </h3>
          <ul className="space-y-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">✓</span>
              <span>Beba pelo menos 2 litros de água por dia</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">✓</span>
              <span>Evite pular refeições para manter o metabolismo ativo</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">✓</span>
              <span>Mastigue bem os alimentos e coma devagar</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-accent mt-1">✓</span>
              <span>Evite frituras e alimentos ultraprocessados</span>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default PlanoAlimentar;